import { useState, useEffect } from 'react'
import { Book, Plus, Search, Edit2, Trash2, X, Save, Eye, Printer, Settings } from 'lucide-react'
import { bookService, mapelService } from '../services/masterService'

function MasterBukuPage() {
  const [books, setBooks] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [filterJenjang, setFilterJenjang] = useState('')
  const [showModal, setShowModal] = useState(false)
  const [showDetailModal, setShowDetailModal] = useState(false)
  const [editingBook, setEditingBook] = useState(null)
  const [selectedBook, setSelectedBook] = useState(null)
  const [deleteConfirm, setDeleteConfirm] = useState(null) // { id, judul, kode }

  useEffect(() => {
    fetchBooks()
  }, [search, filterJenjang])

  const fetchBooks = async () => {
    try {
      setLoading(true)
      const response = await bookService.getAll({ search, jenjang: filterJenjang })
      if (response.success) {
        setBooks(response.data.books || [])
      }
    } catch (error) {
      console.error('Error fetching books:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteClick = (book) => {
    setDeleteConfirm({ id: book.id, judul: book.judul, kode: book.kode })
  }

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm) return
    try {
      await bookService.delete(deleteConfirm.id)
      setDeleteConfirm(null)
      fetchBooks()
    } catch (error) {
      alert('Gagal menghapus: ' + (error.response?.data?.message || error.message))
      setDeleteConfirm(null)
    }
  }

  const handleEdit = (book) => {
    setEditingBook(book)
    setShowModal(true)
  }

  const handleAdd = () => {
    setEditingBook(null)
    setShowModal(true)
  }

  const handleViewDetail = async (book) => {
    try {
      const response = await bookService.getById(book.id)
      if (response.success) {
        setSelectedBook(response.data.book)
        setShowDetailModal(true)
      }
    } catch (error) {
      console.error('Error fetching book detail:', error)
    }
  }

  const handleModalClose = () => {
    setShowModal(false)
    setEditingBook(null)
  }

  const handleModalSuccess = () => {
    handleModalClose()
    fetchBooks()
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm p-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold text-gray-800 flex items-center gap-2">
              <Book className="w-6 h-6 text-indigo-600" />
              Master Buku
            </h1>
            <p className="text-gray-500 text-sm">Kelola data buku untuk SPK</p>
          </div>
          <button
            onClick={handleAdd}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm font-medium"
          >
            <Plus className="w-4 h-4" />
            Tambah Buku
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1 relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Cari kode, judul, penulis..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 border rounded-lg text-sm"
            />
          </div>
          <select
            value={filterJenjang}
            onChange={(e) => setFilterJenjang(e.target.value)}
            className="px-3 py-2 border rounded-lg text-sm"
          >
            <option value="">Semua Jenjang</option>
            <option value="SD">SD</option>
            <option value="SMP">SMP</option>
            <option value="SMA">SMA</option>
            <option value="SMK">SMK</option>
            <option value="UMUM">UMUM</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
            <p className="text-gray-500 mt-2">Memuat data...</p>
          </div>
        ) : books.length === 0 ? (
          <div className="p-8 text-center">
            <Book className="w-12 h-12 text-gray-300 mx-auto mb-2" />
            <p className="text-gray-500">Belum ada data buku</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Kode</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Judul</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Jenjang</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Mapel/Kelas</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Halaman</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Cetak</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Status</th>
                  <th className="px-4 py-3 text-center font-medium text-gray-600">Aksi</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {books.map((book) => (
                  <tr key={book.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded">{book.kode}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-800">{book.judul}</div>
                      {book.penulis && <div className="text-xs text-gray-500">{book.penulis}</div>}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        book.jenjang === 'SD' ? 'bg-green-100 text-green-700' :
                        book.jenjang === 'SMP' ? 'bg-blue-100 text-blue-700' :
                        book.jenjang === 'SMA' ? 'bg-purple-100 text-purple-700' :
                        book.jenjang === 'SMK' ? 'bg-orange-100 text-orange-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {book.jenjang}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {book.mapel && <span>{book.mapel}</span>}
                      {book.kelas && <span className="text-gray-400"> / Kls {book.kelas}</span>}
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {book.jumlah_halaman ? `${book.jumlah_halaman} hal` : '-'}
                    </td>
                    <td className="px-4 py-3">
                      {book.print_count > 0 ? (
                        <span className="flex items-center gap-1 text-indigo-600">
                          <Printer className="w-3 h-3" />
                          {book.print_count}x (c{book.last_cetak})
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded text-xs ${
                        book.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                      }`}>
                        {book.status === 'active' ? 'Aktif' : 'Nonaktif'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => handleViewDetail(book)}
                          className="p-1.5 hover:bg-gray-100 rounded text-gray-500 hover:text-blue-600"
                          title="Lihat Detail"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleEdit(book)}
                          className="p-1.5 hover:bg-gray-100 rounded text-gray-500 hover:text-indigo-600"
                          title="Edit"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteClick(book)}
                          className="p-1.5 hover:bg-gray-100 rounded text-gray-500 hover:text-red-600"
                          title="Hapus"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <BookModal
          book={editingBook}
          onClose={handleModalClose}
          onSuccess={handleModalSuccess}
        />
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedBook && (
        <BookDetailModal
          book={selectedBook}
          onClose={() => { setShowDetailModal(false); setSelectedBook(null) }}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-sm mx-4 shadow-2xl">
            <div className="text-center">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Trash2 className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Hapus Buku?</h3>
              <p className="text-gray-600 mb-1">
                Anda yakin ingin menghapus buku:
              </p>
              <p className="font-medium text-gray-900 mb-1">
                [{deleteConfirm.kode}]
              </p>
              <p className="font-medium text-gray-900 mb-4">
                {deleteConfirm.judul}
              </p>
              <p className="text-sm text-red-600 mb-6">
                Tindakan ini tidak dapat dibatalkan.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="flex-1 py-2 border rounded-lg text-gray-700 hover:bg-gray-100 font-medium"
                >
                  Batal
                </button>
                <button
                  onClick={handleDeleteConfirm}
                  className="flex-1 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium"
                >
                  Ya, Hapus
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ============================================
// BOOK MODAL (Add/Edit) with Nested MapelModal
// ============================================
function BookModal({ book, onClose, onSuccess }) {
  const isEdit = !!book
  const [formData, setFormData] = useState({
    kode: book?.kode || '',
    judul: book?.judul || '',
    jenjang: book?.jenjang || 'SMP',
    mapel: book?.mapel || '',
    kelas: book?.kelas || '',
    kurikulum: book?.kurikulum || 'Merdeka',
    penulis: book?.penulis || '',
    jumlah_halaman: book?.jumlah_halaman || '',
    ukuran: book?.ukuran || 'B5',
    jenis_cover: book?.jenis_cover || 'soft_cover',
    jenis_kertas_isi: book?.jenis_kertas_isi || 'HVS 70gsm',
    jenis_kertas_cover: book?.jenis_kertas_cover || 'Art Carton 260gsm',
    status: book?.status || 'active',
    notes: book?.notes || ''
  })
  const [saving, setSaving] = useState(false)
  const [mapelList, setMapelList] = useState([])
  const [showMapelModal, setShowMapelModal] = useState(false)

  useEffect(() => {
    fetchMapel()
  }, [])

  const fetchMapel = async () => {
    try {
      const response = await mapelService.getAll()
      if (response.success) {
        setMapelList(response.data.mapel || [])
      }
    } catch (error) {
      console.error('Error fetching mapel:', error)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!formData.kode || !formData.judul) {
      return alert('Kode dan Judul wajib diisi')
    }

    setSaving(true)
    try {
      if (isEdit) {
        await bookService.update(book.id, formData)
      } else {
        await bookService.create(formData)
      }
      onSuccess()
    } catch (error) {
      alert('Gagal menyimpan: ' + (error.response?.data?.message || error.message))
    } finally {
      setSaving(false)
    }
  }

  const handleMapelModalClose = () => {
    setShowMapelModal(false)
    fetchMapel() // Refresh mapel list
  }

  return (
    <>
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white w-full max-w-2xl rounded-xl shadow-xl max-h-[90vh] overflow-hidden flex flex-col">
          <div className="p-4 border-b flex items-center justify-between bg-indigo-50">
            <h2 className="font-bold text-lg text-indigo-800">
              {isEdit ? 'Edit Buku' : 'Tambah Buku Baru'}
            </h2>
            <button onClick={onClose} className="p-1 hover:bg-indigo-100 rounded">
              <X className="w-5 h-5 text-indigo-600" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-4 overflow-y-auto flex-1 space-y-4">
            {/* Basic Info */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Kode Buku *</label>
                <input
                  type="text"
                  value={formData.kode}
                  onChange={(e) => setFormData({ ...formData, kode: e.target.value.toUpperCase() })}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                  placeholder="Contoh: MTKA-7"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Jenjang *</label>
                <select
                  value={formData.jenjang}
                  onChange={(e) => setFormData({ ...formData, jenjang: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                  required
                >
                  <option value="SD">SD</option>
                  <option value="SMP">SMP</option>
                  <option value="SMA">SMA</option>
                  <option value="SMK">SMK</option>
                  <option value="UMUM">UMUM</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Judul Buku *</label>
              <input
                type="text"
                value={formData.judul}
                onChange={(e) => setFormData({ ...formData, judul: e.target.value })}
                className="w-full border rounded-lg px-3 py-2 text-sm"
                placeholder="Masukkan judul buku"
                required
              />
            </div>

            {/* Mata Pelajaran - Full width with manage button */}
            <div className="bg-gray-50 p-4 rounded-lg border">
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-gray-700">Mata Pelajaran</label>
                <button
                  type="button"
                  onClick={() => setShowMapelModal(true)}
                  className="flex items-center gap-1 px-3 py-1 text-xs bg-emerald-100 hover:bg-emerald-200 text-emerald-700 rounded-lg border border-emerald-300"
                >
                  <Settings className="w-3 h-3" />
                  <span>Kelola Mapel</span>
                </button>
              </div>
              <select
                value={formData.mapel}
                onChange={(e) => setFormData({ ...formData, mapel: e.target.value })}
                className="w-full border rounded-lg px-3 py-2 text-sm bg-white"
              >
                <option value="">-- Pilih Mata Pelajaran --</option>
                {mapelList.map(m => (
                  <option key={m.id} value={m.nama}>{m.nama}</option>
                ))}
              </select>
              {mapelList.length === 0 && (
                <p className="text-xs text-orange-600 mt-1">Belum ada data mapel. Klik "Kelola Mapel" untuk menambah.</p>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Kelas</label>
                <input
                  type="text"
                  value={formData.kelas}
                  onChange={(e) => setFormData({ ...formData, kelas: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                  placeholder="7, 8, 9, dst"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Kurikulum</label>
                <select
                  value={formData.kurikulum}
                  onChange={(e) => setFormData({ ...formData, kurikulum: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                >
                  <option value="Merdeka">Merdeka</option>
                  <option value="K13">K13</option>
                  <option value="KTSP">KTSP</option>
                  <option value="Lainnya">Lainnya</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Penulis</label>
                <input
                  type="text"
                  value={formData.penulis}
                  onChange={(e) => setFormData({ ...formData, penulis: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                  placeholder="Nama penulis"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Jumlah Halaman</label>
                <input
                  type="number"
                  value={formData.jumlah_halaman}
                  onChange={(e) => setFormData({ ...formData, jumlah_halaman: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                  placeholder="200"
                />
              </div>
            </div>

            {/* Print Specs */}
            <div className="border-t pt-4">
              <h3 className="font-medium text-gray-700 mb-3">Spesifikasi Cetak</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ukuran</label>
                  <select
                    value={formData.ukuran}
                    onChange={(e) => setFormData({ ...formData, ukuran: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                  >
                    <option value="A4">A4 (210 x 297 mm)</option>
                    <option value="A5">A5 (148 x 210 mm)</option>
                    <option value="B5">B5 (176 x 250 mm)</option>
                    <option value="Letter">Letter</option>
                    <option value="Custom">Custom</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Jenis Cover</label>
                  <select
                    value={formData.jenis_cover}
                    onChange={(e) => setFormData({ ...formData, jenis_cover: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                  >
                    <option value="soft_cover">Soft Cover</option>
                    <option value="hard_cover">Hard Cover</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Kertas Isi</label>
                  <input
                    type="text"
                    value={formData.jenis_kertas_isi}
                    onChange={(e) => setFormData({ ...formData, jenis_kertas_isi: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                    placeholder="HVS 70gsm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Kertas Cover</label>
                  <input
                    type="text"
                    value={formData.jenis_kertas_cover}
                    onChange={(e) => setFormData({ ...formData, jenis_kertas_cover: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                    placeholder="Art Carton 260gsm"
                  />
                </div>
              </div>
            </div>

            {/* Status & Notes */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                >
                  <option value="active">Aktif</option>
                  <option value="inactive">Nonaktif</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Catatan</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="w-full border rounded-lg px-3 py-2 text-sm"
                rows={2}
                placeholder="Catatan tambahan (opsional)"
              />
            </div>
          </form>

          <div className="p-4 border-t bg-gray-50 flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2.5 border rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100"
            >
              Batal
            </button>
            <button
              onClick={handleSubmit}
              disabled={saving}
              className="flex-1 py-2.5 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Menyimpan...' : 'Simpan'}
            </button>
          </div>
        </div>
      </div>

      {/* Nested Mapel Modal */}
      {showMapelModal && (
        <MapelModal onClose={handleMapelModalClose} />
      )}
    </>
  )
}

// ============================================
// MAPEL MODAL (Nested inside BookModal)
// ============================================
function MapelModal({ onClose }) {
  const [mapelList, setMapelList] = useState([])
  const [loading, setLoading] = useState(true)
  const [newMapel, setNewMapel] = useState('')
  const [newKode, setNewKode] = useState('')
  const [editingId, setEditingId] = useState(null)
  const [editingNama, setEditingNama] = useState('')
  const [editingKode, setEditingKode] = useState('')
  const [saving, setSaving] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState(null) // { id, nama }

  useEffect(() => {
    fetchMapel()
  }, [])

  const fetchMapel = async () => {
    try {
      setLoading(true)
      const response = await mapelService.getAll()
      if (response.success) {
        setMapelList(response.data.mapel || [])
      }
    } catch (error) {
      console.error('Error fetching mapel:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleAdd = async () => {
    if (!newMapel.trim()) return alert('Nama mata pelajaran wajib diisi')
    if (!newKode.trim()) return alert('Kode mata pelajaran (3 huruf) wajib diisi')
    if (newKode.trim().length < 2 || newKode.trim().length > 5) {
      return alert('Kode harus 2-5 karakter')
    }
    
    setSaving(true)
    try {
      await mapelService.create({ 
        nama: newMapel.trim(), 
        singkatan: newKode.trim().toUpperCase() 
      })
      setNewMapel('')
      setNewKode('')
      fetchMapel()
    } catch (error) {
      alert('Gagal menambah: ' + (error.response?.data?.message || error.message))
    } finally {
      setSaving(false)
    }
  }

  const handleEdit = (mapel) => {
    setEditingId(mapel.id)
    setEditingNama(mapel.nama)
    setEditingKode(mapel.singkatan || '')
  }

  const handleSaveEdit = async () => {
    if (!editingNama.trim()) return alert('Nama mata pelajaran wajib diisi')
    setSaving(true)
    try {
      await mapelService.update(editingId, { 
        nama: editingNama.trim(),
        singkatan: editingKode.trim().toUpperCase() || null
      })
      setEditingId(null)
      setEditingNama('')
      setEditingKode('')
      fetchMapel()
    } catch (error) {
      alert('Gagal menyimpan: ' + (error.response?.data?.message || error.message))
    } finally {
      setSaving(false)
    }
  }

  const handleCancelEdit = () => {
    setEditingId(null)
    setEditingNama('')
    setEditingKode('')
  }

  const handleDeleteClick = (mapel) => {
    setDeleteConfirm({ id: mapel.id, nama: mapel.nama })
  }

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm) return
    try {
      await mapelService.delete(deleteConfirm.id)
      setDeleteConfirm(null)
      fetchMapel()
    } catch (error) {
      alert('Gagal menghapus: ' + (error.response?.data?.message || error.message))
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4">
      <div className="bg-white w-full max-w-lg rounded-xl shadow-xl max-h-[80vh] overflow-hidden flex flex-col">
        <div className="p-4 border-b flex items-center justify-between bg-emerald-50">
          <h2 className="font-bold text-lg text-emerald-800">📚 Master Mata Pelajaran</h2>
          <button onClick={onClose} className="p-1 hover:bg-emerald-100 rounded">
            <X className="w-5 h-5 text-emerald-600" />
          </button>
        </div>

        {/* Add New Mapel Form */}
        <div className="p-4 border-b bg-gray-50">
          <p className="text-sm font-medium text-gray-700 mb-2">Tambah Mata Pelajaran Baru</p>
          <div className="flex gap-2">
            <input
              type="text"
              value={newKode}
              onChange={(e) => setNewKode(e.target.value.toUpperCase().slice(0, 5))}
              className="w-20 border rounded-lg px-2 py-2 text-sm text-center uppercase"
              placeholder="KODE"
              maxLength={5}
            />
            <input
              type="text"
              value={newMapel}
              onChange={(e) => setNewMapel(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleAdd()}
              className="flex-1 border rounded-lg px-3 py-2 text-sm"
              placeholder="Nama mata pelajaran..."
            />
            <button
              onClick={handleAdd}
              disabled={saving || !newMapel.trim() || !newKode.trim()}
              className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 disabled:opacity-50"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-1">Contoh: MTK - Matematika, IPA - Ilmu Pengetahuan Alam</p>
        </div>

        {/* Mapel List */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-emerald-600 mx-auto"></div>
              <p className="text-gray-500 mt-2 text-sm">Memuat...</p>
            </div>
          ) : mapelList.length === 0 ? (
            <div className="text-center py-8 text-gray-500 text-sm">
              Belum ada mata pelajaran
            </div>
          ) : (
            <div className="space-y-2">
              {mapelList.map((mapel) => (
                <div
                  key={mapel.id}
                  className="flex items-center gap-2 p-3 rounded-lg border bg-white hover:bg-gray-50"
                >
                  {editingId === mapel.id ? (
                    <>
                      <input
                        type="text"
                        value={editingKode}
                        onChange={(e) => setEditingKode(e.target.value.toUpperCase().slice(0, 5))}
                        className="w-16 border rounded px-2 py-1 text-sm text-center uppercase"
                        placeholder="KODE"
                      />
                      <input
                        type="text"
                        value={editingNama}
                        onChange={(e) => setEditingNama(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleSaveEdit()}
                        className="flex-1 border rounded px-2 py-1 text-sm"
                        autoFocus
                      />
                      <button
                        onClick={handleSaveEdit}
                        disabled={saving}
                        className="p-1.5 text-green-600 hover:bg-green-50 rounded"
                        title="Simpan"
                      >
                        <Save className="w-4 h-4" />
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        className="p-1.5 text-gray-500 hover:bg-gray-200 rounded"
                        title="Batal"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </>
                  ) : (
                    <>
                      <span className="w-14 text-xs font-mono bg-emerald-100 text-emerald-700 px-2 py-1 rounded text-center">
                        {mapel.singkatan || '-'}
                      </span>
                      <span className="flex-1 text-sm">{mapel.nama}</span>
                      <button
                        onClick={() => handleEdit(mapel)}
                        className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                        title="Edit"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteClick(mapel)}
                        className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                        title="Hapus"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="p-4 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="w-full py-2 border rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100"
          >
            Tutup
          </button>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[70]">
          <div className="bg-white rounded-xl p-6 max-w-sm mx-4 shadow-2xl">
            <div className="text-center">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Trash2 className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Hapus Mata Pelajaran?</h3>
              <p className="text-gray-600 mb-6">
                Anda yakin ingin menghapus <strong>"{deleteConfirm.nama}"</strong>? 
                Tindakan ini tidak dapat dibatalkan.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="flex-1 py-2 border rounded-lg text-gray-700 hover:bg-gray-100 font-medium"
                >
                  Batal
                </button>
                <button
                  onClick={handleDeleteConfirm}
                  className="flex-1 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium"
                >
                  Ya, Hapus
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ============================================
// BOOK DETAIL MODAL
// ============================================
function BookDetailModal({ book, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white w-full max-w-lg rounded-xl shadow-xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-4 border-b flex items-center justify-between bg-indigo-50">
          <h2 className="font-bold text-lg text-indigo-800">Detail Buku</h2>
          <button onClick={onClose} className="p-1 hover:bg-indigo-100 rounded">
            <X className="w-5 h-5 text-indigo-600" />
          </button>
        </div>

        <div className="p-4 overflow-y-auto flex-1 space-y-4">
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-xs text-gray-500 mb-1">Kode</p>
            <p className="font-mono font-bold text-lg">{book.kode}</p>
          </div>

          <div>
            <p className="text-xs text-gray-500 mb-1">Judul</p>
            <p className="font-bold text-lg text-gray-800">{book.judul}</p>
            {book.penulis && <p className="text-sm text-gray-500">oleh {book.penulis}</p>}
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="bg-blue-50 rounded-lg p-3 text-center">
              <p className="text-xs text-blue-600">Jenjang</p>
              <p className="font-bold text-blue-800">{book.jenjang}</p>
            </div>
            <div className="bg-green-50 rounded-lg p-3 text-center">
              <p className="text-xs text-green-600">Kelas</p>
              <p className="font-bold text-green-800">{book.kelas || '-'}</p>
            </div>
            <div className="bg-purple-50 rounded-lg p-3 text-center">
              <p className="text-xs text-purple-600">Halaman</p>
              <p className="font-bold text-purple-800">{book.jumlah_halaman || '-'}</p>
            </div>
          </div>

          <div className="border-t pt-3">
            <p className="font-medium text-gray-700 mb-2">Spesifikasi Cetak</p>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between"><span className="text-gray-500">Ukuran:</span><span>{book.ukuran}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Jenis Cover:</span><span>{book.jenis_cover === 'soft_cover' ? 'Soft Cover' : 'Hard Cover'}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Kertas Isi:</span><span>{book.jenis_kertas_isi}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Kertas Cover:</span><span>{book.jenis_kertas_cover}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Kurikulum:</span><span>{book.kurikulum}</span></div>
            </div>
          </div>

          {book.print_history && book.print_history.length > 0 && (
            <div className="border-t pt-3">
              <p className="font-medium text-gray-700 mb-2">Riwayat Cetak</p>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {book.print_history.map((h, i) => (
                  <div key={i} className="flex items-center justify-between bg-gray-50 rounded p-2 text-sm">
                    <div>
                      <span className="font-mono bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded text-xs">c{h.cetak_ke}</span>
                      <span className="text-gray-500 ml-2">{new Date(h.created_at).toLocaleDateString('id-ID')}</span>
                    </div>
                    <div className="text-right">
                      <span className="font-medium">{h.qty?.toLocaleString()} eks</span>
                      <span className={`ml-2 px-1.5 py-0.5 rounded text-xs ${
                        h.status === 'completed' ? 'bg-green-100 text-green-700' :
                        h.status === 'in_progress' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        {h.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {book.notes && (
            <div className="border-t pt-3">
              <p className="font-medium text-gray-700 mb-1">Catatan</p>
              <p className="text-sm text-gray-600">{book.notes}</p>
            </div>
          )}
        </div>

        <div className="p-4 border-t bg-gray-50">
          <button onClick={onClose} className="w-full py-2.5 border rounded-lg text-sm font-medium">
            Tutup
          </button>
        </div>
      </div>
    </div>
  )
}

export default MasterBukuPage
